/*
 * <accessories-notify>: the accessories collection's "Notify me when available" pop-up.
 * A sold-out card's notify link (data-bis-open) opens it on the collection page instead of leaving for the product
 * page; the link still goes to the product page's sign-up form when this script is not on the page. The shopper
 * picks the sold-out option when the product has more than one, enters an email, and the sign-up goes to Klaviyo's
 * client back-in-stock subscriptions API for that variant: the same Back in Stock list the product page's Klaviyo
 * form fills, so the store's Back in Stock flow emails them when it is back. The confirmation shows only once
 * Klaviyo accepted it; when Klaviyo cannot be reached (an ad blocker) or the page has no Klaviyo site id the
 * shopper sees an error instead of a sign-up that went nowhere.
 *
 * The call is a plain fetch, not the loaded window.klaviyo object: see pdp-ship-notify.js for why that object is
 * never awaited on this theme.
 */
(() => {
  if (window.customElements.get("accessories-notify")) return;

  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // The revision pdp-ship-notify.js sends; Klaviyo retires one two years after its date.
  const KLAVIYO_REVISION = "2026-07-15";
  const KLAVIYO_TIMEOUT_MS = 8000;

  // The store's public Klaviyo site id: the klaviyo.js tag the Klaviyo app embed adds (the account the store
  // actually runs), else the theme setting the section passes.
  function klaviyoCompanyId(fallback) {
    for (const script of document.querySelectorAll(
      'script[src*="klaviyo.com/onsite/js/"]',
    )) {
      const id = new URL(script.src, location.href).searchParams.get(
        "company_id",
      );
      if (id) return id;
    }
    return fallback || "";
  }

  // The sold-out variants a card carries: [{ id, title }], ids as digits.
  function parseVariants(raw) {
    try {
      const list = JSON.parse(raw || "[]");
      return Array.isArray(list)
        ? list
            .map((v) => ({
              id: String(v?.id ?? "").replace(/\D/g, ""),
              title: String(v?.title ?? ""),
            }))
            .filter((v) => v.id)
        : [];
    } catch {
      return [];
    }
  }

  // Klaviyo's client API answers 202 with no body.
  async function subscribeBackInStock(companyId, email, variantId) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), KLAVIYO_TIMEOUT_MS);
    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(companyId)}`,
      {
        method: "POST",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify({
          data: {
            type: "back-in-stock-subscription",
            attributes: {
              profile: { data: { type: "profile", attributes: { email } } },
              channels: ["EMAIL"],
            },
            relationships: {
              variant: {
                data: {
                  type: "catalog-variant",
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      },
    ).finally(() => clearTimeout(timer));
    if (!response.ok)
      throw new Error(`Klaviyo back in stock failed: ${response.status}`);
  }

  class AccessoriesNotify extends HTMLElement {
    connectedCallback() {
      // The children's listeners are added once; only the document one follows the element in and out.
      if (this.bound) {
        document.addEventListener("click", this.onDocumentClick);
        return;
      }
      this.dialog = this.querySelector("[data-bis-dialog]");
      this.form = this.querySelector("[data-bis-form]");
      if (!this.dialog || !this.form) return;
      this.bound = true;
      this.product = this.querySelector("[data-bis-product]");
      this.optionWrap = this.querySelector("[data-bis-option]");
      this.select = this.querySelector("[data-bis-variant]");
      this.error = this.querySelector("[data-bis-error]");
      this.success = this.querySelector("[data-bis-success]");
      this.intro = this.querySelector("[data-bis-intro]");
      this.done = this.querySelector("[data-bis-done]");

      // Delegated: cards rendered later (search results, Load More) open it too.
      this.onDocumentClick = (event) => {
        const trigger = event.target.closest?.("[data-bis-open]");
        if (!trigger) return;
        // A new-tab or new-window click keeps the link to the product page.
        if (
          event.button > 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        )
          return;
        const variants = parseVariants(trigger.dataset.variants);
        if (!variants.length) return; // nothing to sign up for: the link opens the product page
        event.preventDefault();
        this.open(trigger, variants);
      };
      document.addEventListener("click", this.onDocumentClick);
      this.querySelectorAll("[data-bis-close]").forEach((el) =>
        el.addEventListener("click", () => this.close()),
      );
      this.dialog.addEventListener("click", (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.form.addEventListener("submit", (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    disconnectedCallback() {
      if (this.onDocumentClick)
        document.removeEventListener("click", this.onDocumentClick);
    }

    open(trigger, variants) {
      this.trigger = trigger;
      this.variants = variants;
      if (this.product)
        this.product.textContent = trigger.dataset.productTitle || "";
      if (this.select) {
        this.select.replaceChildren(
          ...variants.map((v) => {
            const option = document.createElement("option");
            option.value = v.id;
            option.textContent = v.title;
            return option;
          }),
        );
      }
      if (this.optionWrap) this.optionWrap.hidden = variants.length < 2;
      // Every opening starts on the form, for whichever product it is.
      this.form.hidden = false;
      if (this.intro) this.intro.hidden = false;
      if (this.success) this.success.hidden = true;
      this.showError("");
      if (typeof this.dialog.showModal === "function") this.dialog.showModal();
      else this.dialog.setAttribute("open", "");
      this.form.querySelector('input[name="email"]')?.focus();
    }

    close() {
      if (typeof this.dialog.close === "function") this.dialog.close();
      else this.dialog.removeAttribute("open");
      this.trigger?.focus?.();
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = !message;
    }

    async submit() {
      if (this.sending) return;
      const email = (this.form.elements.namedItem("email")?.value || "").trim();
      if (!EMAIL.test(email))
        return this.showError("Please enter a valid email.");
      const variants = this.variants || [];
      const variantId =
        variants.length > 1 && this.select
          ? this.select.value
          : variants[0]?.id;
      if (!variantId) return this.showError("Please choose an option.");

      this.sending = true;
      this.showError("");
      const submit = this.form.querySelector('[type="submit"]');
      if (submit) submit.disabled = true;
      try {
        const companyId = klaviyoCompanyId(this.dataset.companyId);
        if (!companyId) throw new Error("No Klaviyo site id on the page");
        await subscribeBackInStock(companyId, email, variantId);
        this.form.hidden = true;
        if (this.intro) this.intro.hidden = true;
        if (this.success) this.success.hidden = false;
        this.done?.focus();
      } catch {
        this.showError(
          "Sorry, we couldn't sign you up. Please try again, or turn off your ad blocker for this site.",
        );
      } finally {
        this.sending = false;
        if (submit) submit.disabled = false;
      }
    }
  }

  window.customElements.define("accessories-notify", AccessoriesNotify);
})();
